import {
  Avatar,
  Box,
  Button,
  Center,
  Flex,
  Icon,
  SkeletonText,
  Spinner,
  Table,
  useDisclosure
} from "@chakra-ui/react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"

import { useQuery } from "@tanstack/react-query"
import { PhoneNumberFormat, PhoneNumberUtil } from "google-libphonenumber"
import { lazy, Suspense, useEffect, useMemo, useState } from "react"
import { z } from "zod"
import { type AuthorsOrderBy } from "../../client"
import ActionsMenu from "../../components/Common/ActionsMenu"
import { PaginationFooter } from "../../components/Common/PaginationFooter"
import Sort from "../../components/Common/Sort"
import TabsList from "../../components/Common/TabsList"
import Navbar from "../../components/Common/Navbar"
import { userHasPermission } from "../../utils"
import useCustomToast from "../../hooks/useCustomToast"
import { FiDownload } from "react-icons/fi"
import { NoPermission } from "../../components/UI/no-permission"
import { Tooltip } from "../../components/UI/tooltip"
import { exportAuthors, readAuthors } from "../../api/authors"

const DetailAuthor = lazy(() => import("../../components/Authors/DetailAuthor"))
const AddAuthor = lazy(() => import("../../components/Authors/AddAuthor"))
const PER_PAGE = 25

const authorsSearchSchema = z.object({
  page: z.number().catch(1),
})

export const Route = createFileRoute("/_layout/authors")({
  component: Authors,
  validateSearch: (search) => authorsSearchSchema.parse(search),
})


function AuthorsTable() {
  // Permission check
  const [au_create, au_read, au_update, au_delete] = useMemo(() => {
    return ["create", "read", "update", "delete"].map(
      (action) => userHasPermission([{ perm: `authors_${action}`, is_allowed: false }])[0].is_allowed
    )
  }, [userHasPermission])

  if(!au_read) return <NoPermission/>

  const showToast = useCustomToast()

  const fields = [
    { title: "Nachname", col: "lastname", align: undefined },
    { title: "Vorname", col: "firstname", align: undefined },
    { title: "Kürzel", col: "abbreviation", align: undefined },
    { title: "Telefon", col: null, align: undefined },
    { title: "Mobil", col: null, align: undefined },
    { title: "Email", col: "email", align: undefined },
    { title: "Erstellungsdatum", col: "created_at", align: undefined },
    { title: "Autorengruppe", col: null, align: undefined },
    { title: "Status", col: "is_active", align: undefined },
    { title: "Aktionen", col: null, align: "right" as const },
  ]

  const [searchValue, setSearchValue] = useState<string>("")

  const handleSearchValueChange = (value: string) => {
    setSearchValue(value)
  }

  const { page } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const setPage = (page: number) => navigate({
    search: (prev: { [key: string]: string }) => ({ ...prev, page }),
  })

  // Set page on search value change
  const [prevPage, setPrevPage] = useState<number>(page)
  useEffect(() => {
    if(searchValue == "") {
      setPage(prevPage)
    } else {
      setPrevPage(page)
      setPage(1)
    }
  }, [searchValue])

  // Sorting
  const [columnName, setColumnName] = useState<AuthorsOrderBy>()
  const [orderBy, setOrderBy] = useState<string>("")
  const [isDesc, setIsDesc] = useState<boolean>()

  // Query
  const queryOptions = useMemo(() =>
      readAuthors({
        per_page: PER_PAGE,
        page,
        order_by: columnName,
        descending: isDesc,
        query: searchValue,
      }),
  [page, columnName, isDesc, searchValue])

  const {
    data: authors,
    isPending,
    isPlaceholderData,
  } = useQuery({
    ...queryOptions,
    placeholderData: (prevData) => prevData,
  })

  // Sorting
  const sortTable = (name: AuthorsOrderBy) => {
    if (columnName !== name) {
      setColumnName(name)
      setOrderBy("")
      sorting("")
    } else {
      sorting(orderBy)
    }
  }

  const sorting = (order: string) => {
    switch (order) {
      case "":
        setOrderBy("desc")
        setIsDesc(true)
        break
      case "desc":
        setOrderBy("asc")
        setIsDesc(false)
        break
      case "asc":
        setOrderBy("")
        setIsDesc(undefined)
        setColumnName(undefined)
        break
    }
  }

  const phoneUtil = PhoneNumberUtil.getInstance()
  const PNF = PhoneNumberFormat

  // Open media detail view
  const detailModal = useDisclosure()
  const [authorId, setAuthorId] = useState("")

  const openModal = (id: string) => {
    setAuthorId(id)
    detailModal.onOpen()
  }

  const handleExport = () => {
    exportAuthors({authorgroup_name: undefined, descending: isDesc, order_by: columnName, query: searchValue}, showToast)
  }

  return (
    <>
      <Flex alignItems="center">
        <Box w="full">
          <Navbar
            type="Autor"
            addAllowed={au_create}
            addModalAs={AddAuthor}
            showSearch={true}
            onSearchChange={handleSearchValueChange}
          />
        </Box>

        <Tooltip showArrow content={`Autoren exportieren`} openDelay={500}>
          <Button onClick={handleExport} variant="primary" ml={4} aria-label="Autoren exportieren">
            <Icon as={FiDownload} />
          </Button>
        </Tooltip>
      </Flex>

      {detailModal.open &&
        <Suspense fallback={
          <Center
            bg="rgba(0, 0, 0, 0.3)"
            position="fixed"
            top="0"
            left="0"
            width="100vw"
            height="100vh"
            zIndex="10000"
            pointerEvents="none"
          >
            <Spinner size="lg" color="white" />
          </Center>}
        >
          <DetailAuthor
            isOpen={detailModal.open}
            onClose={detailModal.onClose}
            authorId={authorId}
          />
        </Suspense>
      }

      <Table.ScrollArea asChild>
        <Table.Root size={{ base: "sm", md: "md" }} whiteSpace="normal" display={{md: "table"}}>
          <Table.Header>
            <Table.Row>
              {fields.map((field, i) =>
                field.col != null ? (
                  <Table.ColumnHeader
                    key={i}
                    onClick={() => sortTable(field.col as AuthorsOrderBy)}
                    cursor="pointer"
                    textAlign={field.align}
                  >
                    {field.title}
                    {columnName === field.col &&
                      <Sort
                        columnNamePassed={columnName}
                        orderByPassed={orderBy}
                      />
                    }
                  </Table.ColumnHeader>
                ) : (
                  <Table.ColumnHeader key={i} textAlign={field.align}>
                    {field.title}
                  </Table.ColumnHeader>
                ),
              )}
            </Table.Row>
          </Table.Header>
          {isPending ? (
            <Table.Body>
              <Table.Row>
                {new Array(10).fill(null).map((_, index) => (
                  <Table.Cell key={index}>
                    <SkeletonText noOfLines={1} paddingBlock="16px" />
                  </Table.Cell>
                ))}
              </Table.Row>
            </Table.Body>
          ) : (
            <Table.Body>
              {!authors?.data ? (
                <Table.Row>
                  <Table.Cell colSpan={10} textAlign="center">
                    Keine Autoren verfügbar
                  </Table.Cell>
                </Table.Row>
              ) : (
                authors?.data.map((author, i) => (
                  <Table.Row key={i} opacity={isPlaceholderData ? 0.5 : 1}>
                    <Table.Cell onClick={() => openModal(author.id)} cursor="pointer">
                      <Flex alignItems="center">
                      <Avatar.Root mr={2}>
                        <Avatar.Fallback
                          name={
                            author?.lastname && author?.firstname
                              ? `${author.lastname} ${author.firstname}`
                              : author?.firstname || author?.lastname || author?.abbreviation || ""
                          }
                        />
                        <Avatar.Image src={author.fk_media_profile_pic?.path.replace(/-(LG)\./, "-SM.")} />
                      </Avatar.Root>
                      {author.lastname}
                      </Flex>
                    </Table.Cell>

                    <Table.Cell onClick={() => openModal(author.id)} cursor="pointer">
                      {author.firstname}
                    </Table.Cell>

                    <Table.Cell onClick={() => openModal(author.id)} cursor="pointer">
                      {author.abbreviation}
                    </Table.Cell>

                    {author.phone ? (
                      <Table.Cell onClick={() => openModal(author.id)} cursor="pointer">
                        {phoneUtil.format(
                          phoneUtil.parseAndKeepRawInput(author.phone, "CH"),
                          PNF.NATIONAL,
                        )}
                      </Table.Cell>
                    ) : (
                      <Table.Cell onClick={() => openModal(author.id)} cursor="pointer">
                      </Table.Cell>
                    )}

                    {author.mobile ? (
                      <Table.Cell onClick={() => openModal(author.id)} cursor="pointer">
                        {phoneUtil.format(
                          phoneUtil.parseAndKeepRawInput(author.mobile, "CH"),
                          PNF.NATIONAL,
                        )}
                      </Table.Cell>
                    ) : (
                      <Table.Cell onClick={() => openModal(author.id)} cursor="pointer">
                      </Table.Cell>
                    )}

                    <Table.Cell onClick={() => openModal(author.id)} cursor="pointer">
                      {author.email}
                    </Table.Cell>

                    <Table.Cell onClick={() => openModal(author.id)} cursor="pointer">
                      {new Date(`${author.created_at}`).toLocaleString(
                        "de-CH",
                        { timeZone: "Europe/Zurich" },
                      )}
                    </Table.Cell>

                    <Table.Cell onClick={() => openModal(author.id)} cursor="pointer">
                      {author.fk_author_group?.name}
                    </Table.Cell>

                    <Table.Cell
                      onClick={() => openModal(author.id)}
                      cursor="pointer"
                    >
                      <Flex gap={2}>
                        <Box
                          w="2"
                          h="2"
                          borderRadius="50%"
                          bg={author.is_active ? "success" : "danger"}
                          alignSelf="center"
                        />
                        {author.is_active ? "Aktiv" : "Inaktiv"}
                      </Flex>
                    </Table.Cell>

                    <Table.Cell textAlign="right">
                      <ActionsMenu
                        type="Autor"
                        value={author}
                        disableEdit={!au_update}
                        disableDelete={!au_delete}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          )}
        </Table.Root>
      </Table.ScrollArea>
      <PaginationFooter
        onChangePage={setPage}
        page={page}
        count={authors?.count ? authors?.count : 0}
        perPage={PER_PAGE}
      />
    </>
  )
}

export default AuthorsTable

function Authors() {
  const tabs = useMemo(() => [
      {value: "authors", title: "Autoren"},
      {value: "authorgroups", title: "Autorengruppen"},
  ], [])

  return (
    <TabsList tabs={tabs} defaultVal="authors" />
  )
}
